import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { ChevronDown, Loader2, Minus, Plus } from 'lucide-react'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

const loading = (
  <div className="flex h-48 items-center justify-center">
    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
  </div>
)

const error = (
  <div className="flex h-24 items-center justify-center text-xs text-gray-400">Could not load PDF.</div>
)

function PdfControls({
  page,
  numPages,
  scale,
  onPage,
  onScale,
  showZoom = true,
}: {
  page: number
  numPages: number
  scale: number
  onPage: (p: number) => void
  onScale: (s: number) => void
  showZoom?: boolean
}) {
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-3 py-2">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}
        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30">
        ← Prev
      </button>
      <div className="flex items-center gap-3">
        {showZoom && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onScale(Math.max(0.5, scale - 0.25))}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
              disabled={scale <= 0.5}>
              <Minus className="h-3 w-3" />
            </button>
            <span className="w-10 text-center text-xs text-gray-400">{Math.round(scale * 100)}%</span>
            <button type="button" onClick={() => onScale(Math.min(3, scale + 0.25))}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
              disabled={scale >= 3}>
              <Plus className="h-3 w-3" />
            </button>
          </div>
        )}
        <span className="text-xs text-gray-400">{page} / {numPages}</span>
      </div>
      <button type="button" disabled={page >= numPages} onClick={() => onPage(page + 1)}
        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30">
        Next →
      </button>
    </div>
  )
}

/** The page fitted to the width of the box it sits in, with page controls under it. */
export function PdfInline({ fileUrl }: { fileUrl: string }) {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [inlineWidth, setInlineWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setNumPages(null)
    setPage(1)
  }, [fileUrl])

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return

    const measure = () => {
      const width = element.clientWidth
      if (width > 0) setInlineWidth(width)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [fileUrl])

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 flex-col">
      <div className="relative min-h-48 w-full max-w-full overflow-hidden">
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages)
            setPage(1)
          }}
          loading={loading}
          error={error}
        >
          <Page
            className="max-w-full"
            pageNumber={page}
            width={inlineWidth > 0 ? inlineWidth : 360}
            devicePixelRatio={2}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
      {numPages !== null && numPages > 1 && (
        <PdfControls page={page} numPages={numPages} scale={1} onPage={setPage} onScale={() => {}} showZoom={false} />
      )}
    </div>
  )
}

/** The zoomable, pinchable, pageable view inside the full-screen dialog. */
export function PdfFull({ fileUrl }: { fileUrl: string }) {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [scale, setScale] = useState(2)
  const scaleRef = useRef(2)
  const gestureRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  useEffect(() => {
    const element = gestureRef.current
    if (!element) return

    let pinchInitialDistance = 0
    let pinchInitialScale = 2

    const distance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const applyScale = (next: number) => {
      const clamped = Math.max(0.5, Math.min(8, next))
      scaleRef.current = clamped
      setScale(clamped)
    }

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        pinchInitialDistance = distance(event.touches)
        pinchInitialScale = scaleRef.current
      }
    }

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2) return
      event.preventDefault()
      applyScale(pinchInitialScale * (distance(event.touches) / pinchInitialDistance))
    }

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      applyScale(scaleRef.current + (event.deltaY < 0 ? 0.2 : -0.2))
    }

    element.addEventListener('touchstart', onTouchStart, { passive: true })
    element.addEventListener('touchmove', onTouchMove, { passive: false })
    element.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      element.removeEventListener('touchstart', onTouchStart)
      element.removeEventListener('touchmove', onTouchMove)
      element.removeEventListener('wheel', onWheel)
    }
  }, [])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {numPages !== null && numPages > 1 && (
          <>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              className="absolute left-3 top-1/2 z-10 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur-sm transition-opacity hover:bg-white disabled:opacity-20"
              aria-label="Previous page"
            >
              <ChevronDown className="h-6 w-6 rotate-90 text-gray-700" />
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(numPages, current + 1))}
              disabled={page >= numPages}
              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur-sm transition-opacity hover:bg-white disabled:opacity-20"
              aria-label="Next page"
            >
              <ChevronDown className="h-6 w-6 -rotate-90 text-gray-700" />
            </button>
          </>
        )}
        <div ref={gestureRef} className="h-full overflow-auto bg-gray-100 p-4">
          <div className="mx-auto w-fit">
            <Document
              file={fileUrl}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages)
                setPage(1)
              }}
              loading={loading}
              error={error}
            >
              <Page pageNumber={page} scale={scale} devicePixelRatio={2} renderTextLayer renderAnnotationLayer={false} />
            </Document>
          </div>
        </div>
      </div>
      {numPages !== null && (
        <div className="shrink-0">
          <PdfControls page={page} numPages={numPages} scale={scale} onPage={setPage} onScale={setScale} />
        </div>
      )}
    </div>
  )
}
